import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { InfoNotification, WarningNotification } from '@ardoq/status-ui';
import { Text } from '@ardoq/typography';
import {
  ExternalLink,
  Link,
  OPENS_IN_NEW_TAB_LABEL,
  UnstyledLinkProps,
} from '@ardoq/link';
import { mergeTripleWarningLabels, type TripleWarningLabels } from '../labels';

export type TripleWarningProps = {
  sourceType: string;
  referenceType: string;
  targetType: string;
  /**
   * Number of approved named reference types for this source → target pair,
   * used by the Implicit-type info banner. When omitted or 0, the banner shows
   * the generic "no approved types" nudge instead of a count.
   */
  validReferenceTypeCount?: number;
  canManageMetamodelConstraints: boolean;
  manageConstraintsLinkProps: UnstyledLinkProps;
  /**
   * Optional label overrides. Missing keys fall back to English defaults so
   * existing consumers keep their current copy without changes.
   */
  labels?: Partial<TripleWarningLabels>;
};

export const TripleWarning = ({
  sourceType,
  referenceType,
  targetType,
  validReferenceTypeCount,
  canManageMetamodelConstraints,
  manageConstraintsLinkProps,
  labels: labelOverrides,
}: TripleWarningProps) => {
  const labels = mergeTripleWarningLabels(labelOverrides);

  // Choosing the Implicit type is informational, never an error — it always
  // shows as a calm info banner. When approved named types exist we name the
  // count and point to them; otherwise we show the generic nudge.
  if (referenceType === 'Implicit') {
    const message =
      validReferenceTypeCount && validReferenceTypeCount > 0
        ? labels.implicitReferenceTypeWarningWithCount({
            count: validReferenceTypeCount,
            sourceType,
            targetType,
          })
        : labels.implicitReferenceTypeWarning;

    return (
      <InfoNotification>
        <Text variant="text2">{message}</Text>
      </InfoNotification>
    );
  }

  const detailLeadIn = canManageMetamodelConstraints
    ? labels.referenceNotApprovedAdminDetail
    : labels.referenceNotApprovedNonAdminDetail;

  return (
    <WarningNotification>
      <Text variant="text2">
        {labels.referenceTypeViolatesConstraintsWarning({
          referenceType,
          sourceType,
          targetType,
        })}{' '}
        {detailLeadIn}{' '}
        {canManageMetamodelConstraints ? (
          <Link
            typography="text2"
            hideIcon
            href={manageConstraintsLinkProps.href}
            target={manageConstraintsLinkProps.target}
            onClick={() => {
              manageConstraintsLinkProps.onTrack?.();
              manageConstraintsLinkProps.onClick?.();
            }}
          >
            {labels.metamodelConstraintsInlineLink}
          </Link>
        ) : (
          <ExternalLink
            href={KnowledgeBaseLink.META_MODEL_CONSTRAINTS}
            opensInNewTabLabel={OPENS_IN_NEW_TAB_LABEL}
          >
            {labels.metamodelConstraintsInlineLink}
          </ExternalLink>
        )}
        .
      </Text>
    </WarningNotification>
  );
};
